// =============================================================================
// screens/LeaderOnboardingScreen.tsx — one-time intro for new CRYB Leaders.
//
// Fires the first time an approved leader opens the Leader Dashboard (gated by an
// AsyncStorage flag). A short, onboarding-style paged intro to the leader tools,
// ending in a CTA to create their first community. Distinct from the normal
// signup onboarding — this is the "leader flow" onboarding.
// =============================================================================
import { useCallback, useRef, useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions, TouchableOpacity, FlatList } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Animated, { FadeIn } from 'react-native-reanimated';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D, { Asset3DName } from '../components/ui/Asset3D';
import { GlassButton } from '../components/glass';
import { colors, spacing, fonts, typographyScale } from '../constants/theme';
import type { RootStackParamList } from '../navigation/types';

export const LEADER_ONBOARDING_SEEN_KEY = 'cryb:leader_onboarding_seen';

type Nav = NativeStackNavigationProp<RootStackParamList, 'LeaderOnboarding'>;

interface Pane { asset: Asset3DName; title: string; body: string }
const PANES: Pane[] = [
  { asset: 'medal', title: "You're a CRYB Leader", body: 'Congrats — your application was approved. You can now create and run communities with a full set of creator tools.' },
  { asset: 'community', title: 'Build your community', body: 'Create a space, customize its look, set up channels, and bring your people together around what you care about.' },
  { asset: 'shield', title: 'Moderate & grow', body: 'Manage members and moderators, keep the space healthy, and track how your community grows over time.' },
];

export default function LeaderOnboardingScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const [index, setIndex] = useState(0);
  const listRef = useRef<FlatList<Pane>>(null);

  const finish = useCallback(async (createNow: boolean) => {
    try { await AsyncStorage.setItem(LEADER_ONBOARDING_SEEN_KEY, '1'); } catch { /* non-fatal */ }
    // Replace so Back doesn't return to the intro.
    if (createNow) navigation.replace('CreateCommunity');
    else navigation.goBack();
  }, [navigation]);

  const next = useCallback(() => {
    if (index < PANES.length - 1) {
      const n = index + 1;
      setIndex(n);
      listRef.current?.scrollToIndex({ index: n, animated: true });
    } else {
      finish(false);
    }
  }, [index, finish]);

  const isLast = index === PANES.length - 1;

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <TouchableOpacity
        style={[styles.skip, { top: insets.top + spacing.sm }]}
        onPress={() => finish(false)}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        accessibilityRole="button" accessibilityLabel="Skip"
      >
        <Text style={styles.skipText}>Skip</Text>
      </TouchableOpacity>

      <FlatList
        ref={listRef}
        data={PANES}
        keyExtractor={(_, i) => String(i)}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        scrollEnabled={false}
        renderItem={({ item }) => (
          <Animated.View entering={FadeIn.duration(400)} style={[styles.pane, { width }]}>
            <Asset3D name={item.asset} size={120} style={{ marginBottom: spacing.xl }} />
            <Text style={styles.title}>{item.title}</Text>
            <Text style={styles.body}>{item.body}</Text>
          </Animated.View>
        )}
      />

      <View style={[styles.footer, { paddingBottom: insets.bottom + spacing.lg }]}>
        <View style={styles.dots}>
          {PANES.map((_, i) => (
            <View key={i} style={[styles.dot, i === index && styles.dotActive]} />
          ))}
        </View>
        <GlassButton
          title={isLast ? 'Create my first community' : 'Next'}
          onPress={isLast ? () => finish(true) : next}
          variant="primary"
        />
        {isLast && (
          <TouchableOpacity onPress={() => finish(false)} style={styles.laterBtn} accessibilityRole="button">
            <Text style={styles.laterText}>Maybe later</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  skip: { position: 'absolute', right: spacing.lg, zIndex: 10 },
  skipText: { ...typographyScale.body, color: colors.muted, ...fonts.semibold },
  pane: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: spacing.xxl },
  title: { ...typographyScale.h1, color: colors.ink, ...fonts.bold, textAlign: 'center', marginBottom: spacing.md },
  body: { ...typographyScale.body, color: colors.muted, textAlign: 'center', lineHeight: 24 },
  footer: { paddingHorizontal: spacing.xl },
  dots: { flexDirection: 'row', justifyContent: 'center', gap: 8, marginBottom: spacing.lg },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.cardBorder },
  dotActive: { backgroundColor: colors.primary, width: 22 },
  laterBtn: { alignItems: 'center', paddingVertical: spacing.md },
  laterText: { ...typographyScale.body, color: colors.muted, ...fonts.medium },
});
